import { computed, unref } from "vue";
import useMagnoliaLanguage from "~/composables/useMagnoliaLanguage";
import { formatCurrency } from "~/utils/formatCurrency";

interface UseFormattedCurrencyOptions {
  currency?: string;
  currencyDisplay?: "symbol" | "narrowSymbol" | "code" | "name";
  locale?: string;
  precision?: number;
  showPositivePrefix?: boolean;
}

/**
 * A composable for formatting numbers as percentages with localization support.
 *
 * @param {number|string} value - The value to format as a percentage.
 * @param {UseFormattedCurrencyOptions} options - Formatting options.
 * @returns {object} - An object containing a computed property `formattedValue`.
 */
export const useFormattedCurrency = (
  value: number | string,
  options: UseFormattedCurrencyOptions = {},
) => {
  const { currency } = useSessionContext();
  const { currentLanguage } = useMagnoliaLanguage();

  // Resolve the 'locale' based on the provided options or Magnolia's current language
  const resolvedLocale = computed(
    () => options.locale ?? currentLanguage?.value?.replace("_", "-") ?? "de-DE",
  );

  // Resolve the 'precision' based on the provided options or Shopware's current precision
  const resolvedPrecision = computed(
    () => options.precision ?? currency?.value?.decimalPrecision ?? 2,
  );

  // Compute the formatted value using the formatCurrency utility function
  const formattedValue = computed(() =>
    formatCurrency(unref(value), {
      currency: options.currency ?? "EUR",
      currencyDisplay: options.currencyDisplay ?? "symbol",
      locale: unref(resolvedLocale),
      precision: unref(resolvedPrecision),
      showPositivePrefix: options.showPositivePrefix ?? false,
    }),
  );

  return { formattedValue };
};
